/*
 * Pre-order reservation form (snippets/preorder-form.liquid).
 *
 * No money and no Shopify order — the form only records who wants a copy.
 * The entry fans out to two places, both optional and set in theme settings:
 *
 *   1. the pre-order Google Sheet, through the Apps Script receiver
 *      (see sheets-bridge/README.md)
 *   2. Klaviyo — a list subscription plus a `Pre-ordered` event, so the
 *      confirmation email can run as a flow
 *
 * Whichever is configured first acts as the gate: the reservation is only
 * called done once that call succeeds. The rest is best-effort, so a Klaviyo
 * hiccup never loses an entry that already reached the sheet.
 *
 * With neither configured this script is not loaded at all and the native
 * Shopify contact form handles the submit.
 */
(function () {
  'use strict';

  var KLAVIYO_COMPANY_ID = 'YrmTGY';
  var KLAVIYO_REVISION = '2024-02-15';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function klaviyoSubscribe(listId, attributes) {
    return fetch(
      'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(KLAVIYO_COMPANY_ID),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: { profile: { data: { type: 'profile', attributes: attributes } } },
            relationships: { list: { data: { type: 'list', id: listId } } }
          }
        })
      }
    ).then(checkResponse);
  }

  function klaviyoTrack(metric, email, properties) {
    return fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(KLAVIYO_COMPANY_ID), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            metric: { data: { type: 'metric', attributes: { name: metric } } },
            properties: properties,
            profile: { data: { type: 'profile', attributes: { email: email } } }
          }
        }
      })
    }).then(checkResponse);
  }

  // text/plain keeps the browser from sending a CORS preflight, the same trick
  // the feedback form uses against the same receiver.
  function toBridge(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(payload)
    }).then(checkResponse);
  }

  function checkResponse(res) {
    if (!res.ok) {
      return res.text().then(function (body) {
        throw new Error(res.status + ' ' + body);
      });
    }
    return res;
  }

  function setup(root) {
    var form = root.querySelector('.veride-preorder__form');
    var panel = root.querySelector('[data-veride-preorder-panel]');
    var done = root.querySelector('[data-veride-preorder-done]');
    var openBtn = root.querySelector('[data-veride-preorder-open]');
    if (!form || !panel) return;

    var endpoint = (root.dataset.endpoint || '').trim();
    var listId = (root.dataset.listId || '').trim();
    var newsletterListId = (root.dataset.newsletterListId || '').trim();
    var metric = (root.dataset.metric || 'Pre-ordered').trim();
    if (!endpoint && !listId) return; // native contact form stays in charge

    var plate = root.querySelector('.veride-preorder__plate');
    var nameInput = form.querySelector('[data-po-name]');
    var emailInput = form.querySelector('[data-po-email]');
    var quantityInput = form.querySelector('[data-po-quantity]');
    var countryInput = form.querySelector('[data-po-country]');
    var newsletterInput = form.querySelector('[data-po-newsletter]');
    var honeypot = form.querySelector('[name="po-website"]');
    var submitBtn = form.querySelector('[data-po-submit]');
    var errorEl = form.querySelector('[data-po-error]');
    var doneBody = form.querySelector('[data-po-done-body]');

    if (openBtn) {
      openBtn.addEventListener('click', function () {
        panel.hidden = false;
        openBtn.hidden = true;
        openBtn.setAttribute('aria-expanded', 'true');
        if (nameInput) nameInput.focus();
      });
    }

    function fail(message, field) {
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      }
      if (field) field.focus();
      if (submitBtn) submitBtn.disabled = false;
    }

    function succeed(email) {
      if (plate) plate.hidden = true;
      panel.hidden = true;
      if (openBtn) openBtn.hidden = true;
      if (doneBody && email) {
        var template = doneBody.getAttribute('data-template') || doneBody.textContent;
        doneBody.textContent = template.split('[email]').join(email);
      }
      if (done) done.hidden = false;
      root.setAttribute('data-veride-preorder-state', 'done');
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }

      var name = ((nameInput && nameInput.value) || '').trim();
      var email = ((emailInput && emailInput.value) || '').trim();
      var quantity = parseInt((quantityInput && quantityInput.value) || '1', 10) || 1;
      var country = ((countryInput && countryInput.value) || '').trim();
      var wantsNewsletter = !!(newsletterInput && newsletterInput.checked);

      if (!name) return fail('Please add your name.', nameInput);
      if (!EMAIL_RE.test(email)) return fail('Please enter a valid email address.', emailInput);
      if (!country) return fail('Please choose where it should ship.', countryInput);

      // Honeypot filled — a bot. Show the same success state and send nothing.
      if (honeypot && honeypot.value) return succeed(email);

      if (submitBtn) submitBtn.disabled = true;

      var properties = {
        product_title: root.dataset.productTitle || '',
        product_handle: root.dataset.productHandle || '',
        product_id: root.dataset.productId || '',
        variant_id: root.dataset.variantId || '',
        variant_sku: root.dataset.variantSku || '',
        quantity: quantity,
        country: country,
        price_cents: parseInt(root.dataset.priceCents || '0', 10) || 0,
        currency: root.dataset.currency || ''
      };

      var payload = {
        name: name,
        email: email,
        quantity: quantity,
        country: country,
        newsletter: wantsNewsletter,
        product_title: properties.product_title,
        product_handle: properties.product_handle,
        product_id: properties.product_id,
        variant_id: properties.variant_id,
        variant_sku: properties.variant_sku,
        price_cents: properties.price_cents,
        currency: properties.currency,
        page: window.location.href,
        website: honeypot ? honeypot.value : ''
      };

      // Klaviyo is what the person waits on. It carries every field, the
      // consent and the emails, so a reservation that reaches it is a
      // reservation — the copy in the sheet is for reading the list, and if
      // that is down nothing is actually lost. Only with no list configured
      // does the sheet become the thing worth blocking on.
      function recordReservation() {
        if (listId) {
          return klaviyoSubscribe(listId, { email: email, first_name: name, properties: properties });
        }
        return toBridge(endpoint, payload);
      }

      // The `Pre-ordered` event and the newsletter are extras. The reservation
      // stands without them, so they run after it and never reach the person.
      //
      // One after another, not all at once: on the live page the first call
      // landed every time while the ones fired alongside it did not, even
      // though the same calls answer 202 when sent in a burst from outside a
      // browser. Queueing them costs a moment and removes the question.
      // The newsletter goes first — losing a subscription costs more than
      // losing a row in a report.
      function sendExtras() {
        var chain = Promise.resolve();

        function queue(label, run) {
          chain = chain.then(run).catch(function (err) {
            console.error('[pre-order] ' + label + ' failed:', err && err.message);
          });
        }

        if (endpoint && listId) {
          queue('sheet copy', function () {
            return toBridge(endpoint, payload);
          });
        }
        if (wantsNewsletter && newsletterListId) {
          queue('newsletter subscription', function () {
            return klaviyoSubscribe(newsletterListId, { email: email, first_name: name });
          });
        }
        if (listId) {
          queue('pre-ordered event', function () {
            return klaviyoTrack(metric, email, properties);
          });
        }
      }

      recordReservation()
        .then(function () {
          sendExtras();
          succeed(email);
        })
        .catch(function (err) {
          console.error('[pre-order] could not record the reservation:', err && err.message);
          fail('Something didn\'t work — please try again, or write to customercare@veride.world');
        });
    });
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-veride-preorder]'), setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
